import React from 'react';
import {View} from 'react-native';
import {get} from 'lodash';

import InlineFormattedNumber from '/component/base/FormattedNumber/InlineFormattedNumber';
import Text from '/component/base/Text';
import {Reports} from '/constant/TestIDs';
import {withTranslation} from '/provider/TranslationProvider';
import {generateComponentCallback} from '/util/component.util';
import {getDateUtilDefaultInstance} from '/util/date.util';
import testProperties from '/util/testing/testProperties';

import {styles} from './ExpiringRewards.style';

interface ExpiringRewardsProps {
  tt: (key: string, value?: any) => string;
  item: {
    expiringRewards?: {
      balance: string | number;
      currencyCode: string;
      expiresOn: string;
    };
  };
}

export const ExpiringRewards: React.FC<ExpiringRewardsProps> = ({
  tt = withTranslation.ttDefaultProp,
  item = null,
}) => {
  const dateUtil = getDateUtilDefaultInstance();
  const balance = get(item, 'expiringRewards.balance', '');
  const currencyCode = get(item, 'expiringRewards.currencyCode', '');
  const expiresOn = get(item, 'expiringRewards.expiresOn', '');

  const expiryStyle = dateUtil.isDateTodayOrPast(expiresOn)
    ? styles.expiring
    : null;

  const formattedExpiryDate = dateUtil.formatRelativeDate(expiresOn, 'MMM d');

  const expireInDays = dateUtil.getDaysToToday(expiresOn);
  const isExpiryRelative = dateUtil.isDateWithinRelativeRange(expiresOn);

  let renderedExpiry;

  // The value of `expireInDays` is "number of days from expiry until today".
  // This means if this number of greater than 0, then the expiry is in the past.
  if (expireInDays > 0) {
    if (isExpiryRelative) {
      // Already expired some times in the recent past.
      renderedExpiry = (
        <Text style={expiryStyle}>
          {tt(
            'expiry.past.relative',
            generateComponentCallback({
              component: <>Expired {formattedExpiryDate}</>,
            }),
          )}
        </Text>
      );
    } else {
      // Already expired some times in the distant past.
      renderedExpiry = (
        <Text style={expiryStyle}>
          {tt(
            'expiry.past.date',
            generateComponentCallback({
              component: <>Expired on {formattedExpiryDate}</>,
            }),
          )}
        </Text>
      );
    }
  } else {
    if (isExpiryRelative) {
      // Expiring some times in the near future.
      renderedExpiry = (
        <Text style={expiryStyle}>
          {tt(
            'expiry.future.relative',
            generateComponentCallback({
              component: <>Expires {formattedExpiryDate}</>,
            }),
          )}
        </Text>
      );
    } else {
      // Expiring some times in the distant future.
      renderedExpiry = (
        <Text style={expiryStyle}>
          {tt(
            'expiry.future.days',
            generateComponentCallback({
              component: <>Expires in {expireInDays * -1} days</>,
            }),
          )}
        </Text>
      );
    }
  }

  return (
    <View
      style={styles.inline}
      {...testProperties(Reports.reportClientExpiringRewards)}>
      <InlineFormattedNumber
        style={styles.balance}
        value={balance}
        decimalPlaces={2}
        currencyCode={currencyCode}
        testID={Reports.reportClientExpiringRewardsAmount}
      />
      <Text style={styles.dotBefore}> . </Text>
      <View style={styles.inline}>
        <Text
          style={expiryStyle}
          {...testProperties(Reports.reportClientExpiringRewardsDate)}>
          {renderedExpiry}
        </Text>
      </View>
    </View>
  );
};

export default withTranslation('report.record.expiringRewards')(
  ExpiringRewards,
);
